import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router'
import TopNavigation from '../components/Navigation/TopNavigation'
import IntegrationsController from '../controllers/IntegrationsController'
import Notification from '../utilities/Notification'
import RouteHelper from '../helpers/RouteHelper'
import ERoute from '../ERoute'
import UrlHelper from '../helpers/UrlHelper'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import PageLoader from '../components/Page/PageLoader'
import PageContent from '../components/Page/PageContent'
import IntegrationCard from '../components/Integrations/IntegrationCard'
import { IntegrationType, MarketplaceIntegration } from '../types'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

const CATEGORY_ORDER = ['payment', 'banking', 'accounting', 'email', 'debt_collection']

type FilterTab = 'all' | 'connected' | 'not_connected'

type IProps = RouteComponentProps<any>

const Integrations = (props: IProps) => {
  const { location, history } = props
  const { t } = useTranslation()

  const [didInitialLoad, setDidInitialLoad] = React.useState(false)
  const [integrations, setIntegrations] = React.useState<MarketplaceIntegration[]>([])
  const [activeTab, setActiveTab] = React.useState<FilterTab>('all')

  const CATEGORY_INFO: { [category: string]: { name: string, description: string } } = {
    payment: {
      name: t('Integrations::Payment'),
      description: t('Integrations::Accept and manage online payments from your customers.'),
    },
    banking: {
      name: t('Integrations::Banking'),
      description: t('Integrations::Connect your bank accounts to automatically sync transactions.'),
    },
    accounting: {
      name: t('Integrations::Accounting'),
      description: t('Integrations::Sync your invoices and expenses with your accounting software.'),
    },
    email: {
      name: t('Integrations::Email'),
      description: t('Integrations::Connect your email accounts to send messages and stay in sync.'),
    },
    debt_collection: {
      name: t('Integrations::Debt collection'),
      description: t('Integrations::Automate follow-ups on overdue invoices.'),
    },
  }

  const fetchIntegrations = () => {
    IntegrationsController
      .getIntegrations()
      .then(fetchedIntegrations => {
        setDidInitialLoad(true)
        setIntegrations(fetchedIntegrations)
      })
      .catch(console.error)
  }

  React.useEffect(() => {
    fetchIntegrations()

    const params = UrlHelper.getParams(location.search)
    const providerConnected = params.connectSuccess

    if (providerConnected) {
      if (providerConnected === 'yuki') {
        Notification.notifySuccess(t('Integrations::Yuki successfully connected'))
      } else if (providerConnected === 'mollie') {
        Notification.notifySuccess(t('Integrations::Mollie successfully connected'))
      } else {
        console.error(`Unknown type: ${providerConnected}`)
      }
    }
  }, [])

  const onIntegrationClick = (integration: MarketplaceIntegration) => {
    history.push(RouteHelper.process(ERoute.PATH_INTEGRATION, { type: integration.id }))
  }

  const onIntegrationDisconnect = async (integration: MarketplaceIntegration) => {
    try {
      await IntegrationsController.destroy(integration.id as IntegrationType)
      Notification.notifySuccess(t('Integrations::{{title}} disconnected', { title: integration.title }))
      fetchIntegrations()
    } catch (ex) {
      console.error(ex)
    }
  }

  const filteredIntegrations = integrations.filter(integration => {
    if (activeTab === 'connected') return Boolean(integration.connected)
    if (activeTab === 'not_connected') return !integration.connected
    return true
  })

  const integrationsByCategory: { [category: string]: MarketplaceIntegration[] } = {}

  filteredIntegrations.forEach(integration => {
    const category = integration.category || 'other'

    if (!integrationsByCategory[category]) {
      integrationsByCategory[category] = []
    }

    integrationsByCategory[category].push(integration)
  })

  const categories = CATEGORY_ORDER.filter(category => integrationsByCategory[category]?.length > 0)

  return (
    <>
      <Helmet>
        <title>{t('Integrations::{{__appName}} | Integrations')}</title>
      </Helmet>
      <ScrollToTopOnMount />
      <TopNavigation
        icon='bolt'
        title={t('Integrations::Integrations')}
      />

      <PageContent>
        {!didInitialLoad && <PageLoader />}

        {didInitialLoad && <div className='flex flex-col gap-6'>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as FilterTab)}>
            <TabsList>
              <TabsTrigger value='all'>{t('Integrations::All')}</TabsTrigger>
              <TabsTrigger value='connected'>{t('Integrations::Connected')}</TabsTrigger>
              <TabsTrigger value='not_connected'>{t('Integrations::Not connected')}</TabsTrigger>
            </TabsList>
          </Tabs>

          {categories.length === 0 && (
            <p className='text-sm text-muted-foreground'>{t('Integrations::No integrations found.')}</p>
          )}

          {categories.map(category => (
            <div key={category} className='flex flex-col gap-4'>
              <div className='pb-1'>
                <h2 className='text-lg! font-semibold! text-foreground!'>{CATEGORY_INFO[category].name}</h2>
                <p className='text-sm text-muted-foreground'>{CATEGORY_INFO[category].description}</p>
              </div>

              <div className='grid! grid-cols-1! gap-4 sm:grid-cols-2! xl:grid-cols-3!'>
                {integrationsByCategory[category].map(integration => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onClick={() => onIntegrationClick(integration)}
                    onDisconnect={() => onIntegrationDisconnect(integration)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>}
      </PageContent>
    </>
  )
}

export default Integrations
